import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import SearchResultActions from "./SearchResultActions";
import { hotLoadObject } from "./storeUtils";
import DataManager from "./DataManager";
import LoadObject from "./LoadObject";
import dotProp from "dot-prop-immutable";
import SuperRecordActions from "./SuperRecordActions";
import SearchFormActions from "data/SearchFormActions";
import SearchFormStore from "data/SearchFormStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import userStore from "data/userStore";
import MergeActions from "data/MergeActions";

const buildKey = (key, query, order, pageSize, page, onlyEnabledRecords = false) => {
    if (typeof key !== "string") {
        // assume it's an action (aggregate)
        const a = key;
        key = a.key;
        query = a.query;
        order = a.order;
        pageSize = a.pageSize;
        page = a.page;
        if(a.onlyEnabledRecords != undefined) {
            // if a.onlyEnabledRecords has a value use it.
            // Otherwise use the default value for onlyEnabledRecords.
            onlyEnabledRecords = a.onlyEnabledRecords;
        }
    }
    return `${key}?q=${query}&o=${order}[${page}:${pageSize}]&oe=${onlyEnabledRecords}`;
};

const setPathShape = (state, path, params, data) => dotProp.set(
        state,
        [path],
        {
            params,
            data // could be the raw response data or a LoadObject
        }
    );

const getPathData = (state, path) => {
    const obj = dotProp.get(state, [path]);
    return obj ? obj.data : obj;
};

const getPathParams = (state, path) => {
    const obj = dotProp.get(state, [path]);
    return obj ? obj.params : obj;
};

function freshenSearchCaches(state, key, isStale = lo => true) {
    state = {...state};
    const cacheKeys = new Set();
    for (const k of Object.keys(state)) {
        if (k.indexOf("?") < 0) continue; // it's a key cache
        const bareKey = k.split("?", 2)[0];
        if (bareKey !== key) continue;
        const lo = getPathData(state, k);
        if (!isStale(lo)) continue;
        cacheKeys.add(bareKey);
        delete state[k];
    }
    for (const cacheKey of cacheKeys) {
        const params = getPathParams(state, cacheKey);
        if (params) {
            state = doSearch(
                state,
                params.key,
                params.query,
                params.order,
                params.pageSize,
                params.page,
            );
        }
    }
    return state;
}

function resetSearch(state, key, query, order) {
    const tempKey = buildKey(
        key,
        query,
        order,
        100,
        1,
    );
    const prefix = tempKey.substr(0, tempKey.indexOf("[") + 1);
    const toReset = Object.keys(state)
        .filter(k => k.indexOf(prefix) === 0);
    if (toReset.length === 0) return state;
    state = {...state};
    toReset.forEach(k => delete state[k]);
    return state;
}

function doSearch(state, key, query, order, pageSize, page, onlyEnabledRecords = false) {
    const params = {key, query, order, pageSize, page, onlyEnabledRecords};
    const lo = LoadObject.load(
        DataManager.search(
            key,
            query,
            order,
            pageSize,
            page,
            onlyEnabledRecords
        )
    );
    state = setPathShape(
        state,
        buildKey(
            key,
            query,
            order,
            pageSize,
            page,
            onlyEnabledRecords
        ),
        params,
        lo
    );
    // Set the old cache load object to loading if it exists. Doing this allows
    // the ui to keep the old results visible while showing a spinner overlay.
    // If there is no old cache key just use the actual search LO.
    const cacheLO = getPathData(state, key);
    return setPathShape(
        state,
        key, // cache key
        params,
        cacheLO ? cacheLO.loading() : lo,
    );
}

function doSearchFromStore(fluxContext, state) {
    const [
        key,
        query,
        order,
        pageSize,
        page,
    ] = SearchFormStore.getSearchArgs(fluxContext);
    return doSearch(state, key, query, order, pageSize, page);
}

class SearchResultStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return {};
    }

    reduce(state, action) {
        switch (action.type) {

            case SearchFormActions.INITIALIZE_CONTEXT: {
                if (!action[ACTION_CONTEXT_KEY]) return state;
                const fluxContext = action[ACTION_CONTEXT_KEY];
                this.__dispatcher.waitFor([
                    SearchFormStore.getDispatchToken(),
                ]);
                let initiate = !!action.initiateSearch;
                if (!initiate) {
                    const defaultSearch = userStore.getDefaultSearchConfig();
                    initiate = defaultSearch.enabled
                        && !!defaultSearch.query
                        && SearchFormStore.getKey(fluxContext) === defaultSearch.query.key
                        && SearchFormStore.hasValues(fluxContext);
                }
                if (!initiate) return state;
                if (!action.initiateSearch && !SearchFormStore.hasValues(fluxContext)) {
                    // Don't do an unconstrained query, unless _explicitly_
                    // requested. This will avoid unbounded searches in the face
                    // of a user's default search becoming obsolete due to
                    // metamodel changes.
                    return state;
                }
                return doSearchFromStore(fluxContext, state);
            }

            case SearchFormActions.INITIATE_SEARCH:
            case SearchFormActions.INITIATE_SEARCH_AND_SAVE_DEFAULT: {
                this.__dispatcher.waitFor([
                    SearchFormStore.getDispatchToken(),
                ]);
                return doSearchFromStore(action[ACTION_CONTEXT_KEY], state);
            }

            case SearchFormActions.SORT:
            case SearchFormActions.SELECT_PAGE:
            case SearchFormActions.RESTORE_DEFAULT_AND_INITIATE_SEARCH: {
                this.__dispatcher.waitFor([
                    SearchFormStore.getDispatchToken(),
                ]);
                return doSearchFromStore(action[ACTION_CONTEXT_KEY], state);
            }

            case SearchResultActions.START_SEARCH: {
                const {
                    key,
                    query,
                    order,
                    pageSize,
                    page,
                    onlyEnabledRecords,
                } = action;
                return doSearch(state, key, query, order, pageSize, page, onlyEnabledRecords);
            }

            case SearchResultActions.RESET_SEARCH: {
                const {
                    key,
                    query,
                    order,
                } = action;
                return resetSearch(state, key, query, order);
            }

            case SearchResultActions.SEARCH_RESULT: {
                const params = {
                    key: action.key,
                    query: action.query,
                    order: action.order,
                    pageSize: action.pageSize,
                    page: action.page,
                };
                state = setPathShape(
                    state,
                    action.key, // cache key
                    params,
                    LoadObject.empty().receiveValue(action.data) // fake LO
                );
                const path = buildKey(action);
                const lo = getPathData(state, path);
                if (lo == null) return state;
                if (lo.receiveValue == null) debugger;
                return setPathShape(
                    state,
                    path,
                    params,
                    lo.receiveValue(action.data)
                );
            }

            case SearchResultActions.SEARCH_ERROR: {
                const path = buildKey(action);
                const lo = getPathData(state, path);
                if (lo == null) return state;
                const params = getPathParams(state, path);
                state = setPathShape(
                    state,
                    action.key, // cache key
                    params,
                    LoadObject.empty().receiveError(action.error) // fake LO
                );
                return setPathShape(
                    state,
                    path,
                    params,
                    lo.receiveError(action.error)
                );
            }

            case SuperRecordActions.CREATED: {
                return freshenSearchCaches(state, action.key);
            }

            case SuperRecordActions.UPDATED:
            case SuperRecordActions.CANCEL_FOR_OPTLOCK:
            case MergeActions.CANCEL_FOR_SOURCE_OPTLOCK: {
                return freshenSearchCaches(state, action.key, lo =>
                    !lo.hasValue() || lo.getValueEnforcing().results.some(r =>
                    r.id === action.id));
            }

            case SuperRecordActions.DELETED: {
                let next = {
                    ...state,
                };
                for (const k of Object.keys(next)) {
                    if (k.indexOf("?") < 0) continue; // it's a key cache
                    const bareKey = k.split("?", 2)[0];
                    const lo = getPathData(next, k);
                    const isLastForKey = lo.getValueEnforcing() === getPathData(next, bareKey).getValueEnforcing();
                    if (!lo.hasValue()) {
                        delete next[k];
                        if (isLastForKey) {
                            delete next[bareKey];
                        }
                        continue;
                    }
                    const params = getPathParams(next, k);
                    next = setPathShape(
                        next,
                        k,
                        params,
                        lo.map(result => {
                            const countBefore = result.results.length;
                            const filtered = {
                                ...result,
                                results: result.results.filter(r => r.id !== action.id)
                            };
                            const countAfter = filtered.results.length;
                            if (countBefore === countAfter) return result; // nothing changed
                            filtered.totalResultCount -= countBefore - countAfter;
                            return filtered;
                        })
                    );
                    if (isLastForKey) { // update cache key
                        next = setPathShape(
                            next,
                            bareKey,
                            params,
                            getPathData(next, k)
                        );
                    }
                }
                return next;
            }

            default:
                return state;
        }
    }

    getSearchLO(key, query, order, pageSize = 100, page = 1, showLoadingModal = true, onlyEnabledRecords = false) {
        /*
        If we were a bit smarter here, we could track an LO per key/query/order,
        building up a growing number of results over time, and service
        page/pageSize via slicing of the array, instead of making separate
        requests for redundant data. But for now, not doing that is still
        appreciably more efficient than the behaviour to this point.
         */
        const path = buildKey(key, query, order, pageSize, page, onlyEnabledRecords);
        return hotLoadObject(
            () => getPathData(this.getState(), path),
            () => Dispatcher.dispatch({
                type: SearchResultActions.START_SEARCH,
                key,
                query,
                order,
                pageSize,
                page,
                showLoadingModal,
                onlyEnabledRecords
            }),
        );
    }

    isMostRecentSearchLoading(key) {
        const lo = getPathData(this.getState(), key);
        return lo ? lo.isLoading() : false;

    }

    getMostRecentSearch(key) {
        const lo = getPathData(this.getState(), key);
        return lo ? lo.getValue() : undefined;
    }

}

export default new SearchResultStore();



// WEBPACK FOOTER //
// ./app/data/SearchResultStore.js